import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { AppLayout } from '@/components/layout/AppLayout';
import { showcaseAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Store, Plus, Pencil, Trash2, Save, ExternalLink, Star, Mail, Loader2,
  Briefcase, GraduationCap, Wrench, Quote, LayoutTemplate, Eye, CalendarCheck,
  Sparkles, BookOpen,
} from 'lucide-react';
import TrainingSessionsContent from '@/pages/training/TrainingSessionsContent';

const TEMPLATES = [
  { value: 'modern', label: 'Moderne (clair, bleu/teal)' },
  { value: 'dark_tech', label: 'Dark Tech (sombre, néon)' },
  { value: 'minimal', label: 'Minimal (épuré)' },
];

// Normalise un message d'erreur API en string (jamais un objet → évite React #31)
function errMsg(e, fallback) {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map(x => (x?.msg ? `${(x.loc || []).slice(-1)[0] || ''} ${x.msg}`.trim() : JSON.stringify(x))).join(' · ');
  if (typeof d === 'string') return d;
  if (d && typeof d === 'object') return JSON.stringify(d);
  return fallback;
}

const SECTION_LABELS = {
  formations: 'Formations',
  projects: 'Projets',
  services: 'Services',
  experience: 'Parcours / Expérience',
  testimonials: 'Témoignages',
};

// ---- Generic field input ----
function FieldInput({ field, value, onChange }) {
  const v = value ?? (field.type === 'switch' ? false : field.type === 'number' ? '' : '');
  if (field.type === 'textarea') {
    return <Textarea value={v} placeholder={field.placeholder} rows={field.rows || 4}
      onChange={(e) => onChange(e.target.value)} />;
  }
  if (field.type === 'number') {
    return <Input type="number" value={v} placeholder={field.placeholder}
      onChange={(e) => onChange(e.target.value === '' ? null : parseFloat(e.target.value))} />;
  }
  if (field.type === 'switch') {
    return <Switch checked={!!v} onCheckedChange={onChange} />;
  }
  if (field.type === 'tags') {
    const str = Array.isArray(v) ? v.join(', ') : (v || '');
    return <Input value={str} placeholder={field.placeholder || 'séparés par des virgules'}
      onChange={(e) => onChange(e.target.value.split(',').map(s => s.trim()).filter(Boolean))} />;
  }
  if (field.type === 'select') {
    return (
      <Select value={v || field.options?.[0]?.value} onValueChange={onChange}>
        <SelectTrigger><SelectValue /></SelectTrigger>
        <SelectContent>
          {field.options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }
  if (field.type === 'image') {
    return (
      <div className="space-y-2">
        <Input value={v} placeholder="https://… (URL de l'image)" onChange={(e) => onChange(e.target.value)} />
        {v ? <img src={v} alt="" className="h-20 rounded-lg object-cover border" onError={(e) => (e.target.style.display = 'none')} /> : null}
      </div>
    );
  }
  return <Input value={v} placeholder={field.placeholder} onChange={(e) => onChange(e.target.value)} />;
}

// ---- Generic CRUD section ----
function EntitySection({ icon: Icon, title, description, api, fields, emptyLabel, renderSubtitle, aiKind, aiTitleField = 'title' }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  const generateAI = async () => {
    const title = form[aiTitleField];
    if (!title) { toast.error('Renseigne d\'abord le titre'); return; }
    setGenerating(true);
    try {
      const res = await showcaseAPI.aiGenerate({
        kind: aiKind,
        title,
        context: form.summary || form.subtitle || form.description || '',
      });
      const generated = res.data?.fields || {};
      const fieldMap = Object.fromEntries(fields.map(x => [x.name, x]));
      const patch = {};
      Object.entries(generated).forEach(([k, v]) => {
        const f = fieldMap[k];
        if (!f || v == null || v === '') return;
        let val = v;
        if (f.type === 'tags') {
          if (Array.isArray(val)) val = val.map(x => (typeof x === 'string' ? x : (x?.name || x?.title || String(x))));
          else if (typeof val === 'string') val = val.split(',').map(s => s.trim()).filter(Boolean);
          else val = [String(val)];
        } else if (f.type === 'number') {
          const n = parseFloat(val);
          val = isNaN(n) ? null : n;
        } else if (typeof val === 'object') {
          val = Array.isArray(val) ? val.join(', ') : JSON.stringify(val);
        } else {
          val = String(val);
        }
        if (val != null && val !== '') patch[k] = val;
      });
      if (Object.keys(patch).length === 0) {
        toast.message("L'IA n'a rien renvoyé d'exploitable, réessaie avec plus de contexte.");
      } else {
        setForm(prev => ({ ...prev, ...patch }));
        toast.success('Contenu généré ✨');
      }
    } catch (e) {
      toast.error(errMsg(e, 'Erreur IA (vérifie la clé LLM dans Config IA)'));
    } finally {
      setGenerating(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.list();
      setItems(res.data || []);
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openNew = () => {
    const init = {};
    fields.forEach(f => { init[f.name] = f.default !== undefined ? f.default : (f.type === 'switch' ? false : f.type === 'tags' ? [] : ''); });
    setForm(init); setEditing(null); setOpen(true);
  };
  const openEdit = (item) => { setForm({ ...item }); setEditing(item); setOpen(true); };

  const save = async () => {
    // validation: required fields
    for (const f of fields) {
      if (f.required && (form[f.name] === undefined || form[f.name] === '' || form[f.name] === null)) {
        toast.error(`Champ requis : ${f.label}`); return;
      }
    }
    setSaving(true);
    // Nettoyage du payload : nombres vides -> null, tags string -> array
    const payload = { ...form };
    fields.forEach(f => {
      const val = payload[f.name];
      if (f.type === 'number' && (val === '' || val === undefined)) payload[f.name] = null;
      if (f.type === 'tags' && typeof val === 'string') payload[f.name] = val.split(',').map(s => s.trim()).filter(Boolean);
    });
    try {
      if (editing) { await api.update(editing.id, payload); toast.success('Modifié'); }
      else { await api.create(payload); toast.success('Ajouté'); }
      setOpen(false); load();
    } catch (e) {
      toast.error(errMsg(e, 'Erreur à l\'enregistrement'));
    } finally { setSaving(false); }
  };

  const remove = async (item) => {
    if (!window.confirm('Supprimer cet élément ?')) return;
    try { await api.delete(item.id); toast.success('Supprimé'); load(); }
    catch { toast.error('Erreur suppression'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2"><Icon className="h-5 w-5 text-primary" /> {title}</h3>
          {description && <p className="text-sm text-muted-foreground">{description}</p>}
        </div>
        <Button onClick={openNew}><Plus className="h-4 w-4 mr-1" /> Ajouter</Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : items.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-muted-foreground">{emptyLabel || 'Aucun élément.'}</CardContent></Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {items.map(item => (
            <Card key={item.id} className="group">
              <CardContent className="p-4 flex gap-3 items-start">
                {(item.cover_image || item.avatar || item.logo) && (
                  <img src={item.cover_image || item.avatar || item.logo} alt="" className="h-14 w-14 rounded-lg object-cover border flex-shrink-0" onError={(e) => (e.target.style.display = 'none')} />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium truncate">{item.title || item.role || item.author_name}</p>
                    {item.featured && <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" />}
                    {item.status === 'draft' && <Badge variant="outline" className="text-[10px]">brouillon</Badge>}
                  </div>
                  {renderSubtitle && <p className="text-xs text-muted-foreground truncate">{renderSubtitle(item)}</p>}
                </div>
                <div className="flex gap-1 opacity-70 group-hover:opacity-100">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(item)}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(item)}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Modifier' : 'Ajouter'} — {title}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {aiKind && (
              <Button type="button" variant="outline" onClick={generateAI} disabled={generating}
                className="w-full border-primary/40 text-primary hover:bg-primary/5">
                {generating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Sparkles className="h-4 w-4 mr-1" />}
                Générer le contenu avec l'IA
              </Button>
            )}
            {fields.map(f => (
              <div key={f.name} className={f.type === 'switch' ? 'flex items-center justify-between' : 'space-y-1.5'}>
                <Label>{f.label}{f.required && <span className="text-rose-500"> *</span>}</Label>
                <FieldInput field={f} value={form[f.name]} onChange={(val) => setForm(prev => ({ ...prev, [f.name]: val }))} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const STATUS_FIELD = { name: 'status', label: 'Statut', type: 'select', default: 'published', options: [{ value: 'published', label: 'Publié' }, { value: 'draft', label: 'Brouillon' }] };
const ORDER_FIELD = { name: 'order', label: 'Ordre d\'affichage', type: 'number', default: 0 };

// ---- Settings (Général) tab ----
function GeneralTab() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    showcaseAPI.getSettings().then(r => {
      const data = r.data;
      if (!data.sections || data.sections.length === 0) {
        data.sections = Object.keys(SECTION_LABELS).map((k, i) => ({ key: k, enabled: true, order: i, title: SECTION_LABELS[k] }));
      }
      if (!data.social) data.social = {};
      setS(data);
    }).catch(() => toast.error('Erreur chargement settings')).finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setS(prev => ({ ...prev, [k]: v }));
  const setSocial = (k, v) => setS(prev => ({ ...prev, social: { ...prev.social, [k]: v } }));
  const setSection = (key, patch) => setS(prev => ({ ...prev, sections: prev.sections.map(sec => sec.key === key ? { ...sec, ...patch } : sec) }));

  const save = async () => {
    setSaving(true);
    try { await showcaseAPI.updateSettings(s); toast.success('Vitrine enregistrée ✅'); }
    catch (e) { toast.error(errMsg(e, 'Erreur à l\'enregistrement')); }
    finally { setSaving(false); }
  };

  if (loading || !s) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><LayoutTemplate className="h-4 w-4" /> Apparence</CardTitle></CardHeader>
        <CardContent className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label>Template</Label>
            <Select value={s.template || 'modern'} onValueChange={(v) => set('template', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{TEMPLATES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Couleur d'accent (optionnel)</Label>
            <Input type="text" placeholder="#0ea5b7" value={s.accent_color || ''} onChange={(e) => set('accent_color', e.target.value)} />
          </div>
          <div className="flex items-center justify-between sm:col-span-2 border rounded-lg px-3 py-2">
            <div><Label>Vitrine publiée</Label><p className="text-xs text-muted-foreground">Si désactivé, /vitrine renvoie 404.</p></div>
            <Switch checked={!!s.published} onCheckedChange={(v) => set('published', v)} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Hero (bandeau d'accueil)</CardTitle></CardHeader>
        <CardContent className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5 sm:col-span-2"><Label>Titre</Label><Input value={s.hero_title || ''} onChange={(e) => set('hero_title', e.target.value)} placeholder="Houcem Khlifi — Formateur Cloud & DevSecOps" /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Sous-titre</Label><Textarea rows={2} value={s.hero_subtitle || ''} onChange={(e) => set('hero_subtitle', e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Tagline (petit texte)</Label><Input value={s.hero_tagline || ''} onChange={(e) => set('hero_tagline', e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Photo / logo (URL)</Label><Input value={s.hero_avatar || ''} onChange={(e) => set('hero_avatar', e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Image de fond (URL)</Label><Input value={s.hero_background || ''} onChange={(e) => set('hero_background', e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Bouton — libellé</Label><Input value={s.hero_cta_label || ''} onChange={(e) => set('hero_cta_label', e.target.value)} placeholder="Voir les formations" /></div>
          <div className="space-y-1.5"><Label>Bouton — lien</Label><Input value={s.hero_cta_url || ''} onChange={(e) => set('hero_cta_url', e.target.value)} placeholder="#formations" /></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Sections affichées</CardTitle><CardDescription>Active/désactive et renomme les sections de la vitrine.</CardDescription></CardHeader>
        <CardContent className="space-y-2">
          {s.sections.sort((a, b) => a.order - b.order).map(sec => (
            <div key={sec.key} className="flex items-center gap-3 border rounded-lg px-3 py-2">
              <Switch checked={sec.enabled} onCheckedChange={(v) => setSection(sec.key, { enabled: v })} />
              <span className="w-40 text-sm text-muted-foreground">{SECTION_LABELS[sec.key] || sec.key}</span>
              <Input className="flex-1" value={sec.title || ''} placeholder={SECTION_LABELS[sec.key]} onChange={(e) => setSection(sec.key, { title: e.target.value })} />
              <Input type="number" className="w-20" value={sec.order} onChange={(e) => setSection(sec.key, { order: parseInt(e.target.value) || 0 })} />
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Mail className="h-4 w-4" /> Contact & réseaux</CardTitle></CardHeader>
        <CardContent className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label>Email de contact</Label><Input value={s.contact_email || ''} onChange={(e) => set('contact_email', e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Téléphone</Label><Input value={s.contact_phone || ''} onChange={(e) => set('contact_phone', e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Localisation</Label><Input value={s.location || ''} onChange={(e) => set('location', e.target.value)} /></div>
          <div className="space-y-1.5"><Label>LinkedIn</Label><Input value={s.social?.linkedin || ''} onChange={(e) => setSocial('linkedin', e.target.value)} /></div>
          <div className="space-y-1.5"><Label>GitHub</Label><Input value={s.social?.github || ''} onChange={(e) => setSocial('github', e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Facebook</Label><Input value={s.social?.facebook || ''} onChange={(e) => setSocial('facebook', e.target.value)} /></div>
          <div className="space-y-1.5"><Label>YouTube</Label><Input value={s.social?.youtube || ''} onChange={(e) => setSocial('youtube', e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Site web</Label><Input value={s.social?.website || ''} onChange={(e) => setSocial('website', e.target.value)} /></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">SEO / Partage social</CardTitle><CardDescription>Utilisé pour l'aperçu quand tu partages /vitrine sur LinkedIn/Facebook.</CardDescription></CardHeader>
        <CardContent className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label>Titre SEO</Label><Input value={s.seo_title || ''} onChange={(e) => set('seo_title', e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Image de partage (URL)</Label><Input value={s.seo_image || ''} onChange={(e) => set('seo_image', e.target.value)} /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Description SEO</Label><Textarea rows={2} value={s.seo_description || ''} onChange={(e) => set('seo_description', e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Workspace des formations (optionnel)</Label><Input value={s.formations_workspace_id || ''} onChange={(e) => set('formations_workspace_id', e.target.value)} placeholder="vide = toutes les sessions publiées" /></div>
        </CardContent>
      </Card>

      <div className="flex justify-end sticky bottom-0 bg-background/80 backdrop-blur py-3">
        <Button size="lg" onClick={save} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Enregistrer la vitrine</Button>
      </div>
    </div>
  );
}

// ---- Leads tab ----
function LeadsTab() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => { showcaseAPI.listLeads().then(r => setLeads(r.data || [])).catch(() => toast.error('Erreur')).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  const mark = async (l) => { try { await showcaseAPI.markLead(l.id, !l.handled); load(); } catch { toast.error('Erreur'); } };
  const remove = async (l) => { if (!window.confirm('Supprimer ce lead ?')) return; try { await showcaseAPI.deleteLead(l.id); load(); } catch { toast.error('Erreur'); } };
  if (loading) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (leads.length === 0) return <Card><CardContent className="py-10 text-center text-muted-foreground">Aucun lead pour le moment.</CardContent></Card>;
  return (
    <div className="space-y-3">
      {leads.map(l => (
        <Card key={l.id} className={l.handled ? 'opacity-60' : ''}>
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium">{l.name} <span className="text-muted-foreground font-normal">· {l.email}</span></p>
                {l.subject && <p className="text-sm font-medium mt-0.5">{l.subject}</p>}
                <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{l.message}</p>
                <div className="flex gap-2 mt-2 text-xs text-muted-foreground">
                  {l.phone && <span>{l.phone}</span>}
                  {l.context && <Badge variant="outline" className="text-[10px]">{l.context}</Badge>}
                  <span>{new Date(l.created_at).toLocaleString('fr-FR')}</span>
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <Button size="sm" variant={l.handled ? 'outline' : 'default'} onClick={() => mark(l)}>{l.handled ? 'Rouvrir' : 'Traité'}</Button>
                <Button size="sm" variant="ghost" onClick={() => remove(l)}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function ShowcaseAdminPage() {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const vitrineUrl = `${window.location.origin}/vitrine`;

  const projectFields = [
    { name: 'title', label: 'Titre', type: 'text', required: true },
    { name: 'summary', label: 'Résumé court', type: 'text' },
    { name: 'description', label: 'Description', type: 'textarea' },
    { name: 'cover_image', label: 'Image de couverture', type: 'image' },
    { name: 'tech_stack', label: 'Stack techno', type: 'tags' },
    { name: 'category', label: 'Catégorie', type: 'text' },
    { name: 'client', label: 'Client', type: 'text' },
    { name: 'demo_url', label: 'Lien démo', type: 'text' },
    { name: 'repo_url', label: 'Lien dépôt (GitHub)', type: 'text' },
    { name: 'featured', label: 'Mis en avant', type: 'switch' },
    ORDER_FIELD, STATUS_FIELD,
  ];
  const experienceFields = [
    { name: 'role', label: 'Poste / Titre', type: 'text', required: true },
    { name: 'company', label: 'Entreprise / Organisme', type: 'text' },
    { name: 'type', label: 'Type', type: 'select', default: 'experience', options: [{ value: 'experience', label: 'Expérience' }, { value: 'education', label: 'Formation' }, { value: 'certification', label: 'Certification' }] },
    { name: 'location', label: 'Lieu', type: 'text' },
    { name: 'period_start', label: 'Début (ex 2022)', type: 'text' },
    { name: 'period_end', label: 'Fin (vide = Aujourd\'hui)', type: 'text' },
    { name: 'description', label: 'Description', type: 'textarea' },
    { name: 'logo', label: 'Logo (URL)', type: 'image' },
    { name: 'tags', label: 'Compétences', type: 'tags' },
    ORDER_FIELD, STATUS_FIELD,
  ];
  const serviceFields = [
    { name: 'title', label: 'Titre du service', type: 'text', required: true },
    { name: 'icon', label: 'Icône (emoji ou nom lucide)', type: 'text' },
    { name: 'description', label: 'Description', type: 'textarea' },
    { name: 'features', label: 'Points inclus', type: 'tags' },
    { name: 'price_from', label: 'Prix à partir de', type: 'number' },
    { name: 'currency', label: 'Devise', type: 'select', default: 'EUR', options: [{ value: 'EUR', label: 'EUR' }, { value: 'USD', label: 'USD' }, { value: 'TND', label: 'TND' }] },
    { name: 'cta_type', label: 'Type de bouton', type: 'select', default: 'contact', options: [{ value: 'contact', label: 'Contact / devis' }, { value: 'stripe', label: 'Paiement Stripe (lien)' }, { value: 'link', label: 'Lien externe' }] },
    { name: 'cta_label', label: 'Libellé du bouton', type: 'text' },
    { name: 'cta_url', label: 'Lien Stripe / externe', type: 'text', placeholder: 'https://buy.stripe.com/…' },
    { name: 'featured', label: 'Mis en avant', type: 'switch' },
    ORDER_FIELD, STATUS_FIELD,
  ];
  const testimonialFields = [
    { name: 'author_name', label: 'Nom', type: 'text', required: true },
    { name: 'author_role', label: 'Rôle', type: 'text' },
    { name: 'company', label: 'Entreprise', type: 'text' },
    { name: 'avatar', label: 'Avatar (URL)', type: 'image' },
    { name: 'quote', label: 'Témoignage', type: 'textarea', required: true },
    { name: 'rating', label: 'Note (1-5)', type: 'number', default: 5 },
    ORDER_FIELD, STATUS_FIELD,
  ];
  const formationFields = [
    { name: 'title', label: 'Titre de la formation', type: 'text', required: true },
    { name: 'subtitle', label: 'Accroche', type: 'text' },
    { name: 'description', label: 'Description', type: 'textarea' },
    { name: 'cover_image', label: 'Image de couverture', type: 'image' },
    { name: 'level', label: 'Niveau (Débutant / Intermédiaire / Avancé)', type: 'text' },
    { name: 'duration', label: 'Durée (ex 5 jours, 30h)', type: 'text' },
    { name: 'modules', label: 'Modules / chapitres', type: 'tags' },
    { name: 'price_from', label: 'Prix à partir de', type: 'number' },
    { name: 'currency', label: 'Devise', type: 'select', default: 'EUR', options: [{ value: 'EUR', label: 'EUR' }, { value: 'USD', label: 'USD' }, { value: 'TND', label: 'TND' }] },
    { name: 'cta_type', label: 'Type de bouton', type: 'select', default: 'contact', options: [{ value: 'contact', label: 'Contact / devis' }, { value: 'stripe', label: 'Paiement Stripe (lien)' }, { value: 'link', label: 'Lien (inscription / externe)' }] },
    { name: 'cta_label', label: 'Libellé du bouton', type: 'text' },
    { name: 'cta_url', label: 'Lien Stripe / inscription', type: 'text', placeholder: 'https://…' },
    { name: 'featured', label: 'Mise en avant', type: 'switch' },
    ORDER_FIELD, STATUS_FIELD,
  ];

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto p-4 lg:p-6 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2"><Store className="h-6 w-6 text-primary" /> Vitrine</h1>
            <p className="text-sm text-muted-foreground">Ta boutique + portfolio public, pilotable ici.</p>
          </div>
          <Button variant="outline" asChild>
            <a href={vitrineUrl} target="_blank" rel="noreferrer"><Eye className="h-4 w-4 mr-1" /> Voir la vitrine <ExternalLink className="h-3.5 w-3.5 ml-1" /></a>
          </Button>
        </div>

        <Tabs defaultValue="general">
          <TabsList className="flex flex-wrap h-auto">
            <TabsTrigger value="general"><LayoutTemplate className="h-4 w-4 mr-1" /> Général</TabsTrigger>
            <TabsTrigger value="sessions"><CalendarCheck className="h-4 w-4 mr-1" /> Sessions</TabsTrigger>
            <TabsTrigger value="formations"><BookOpen className="h-4 w-4 mr-1" /> Formations</TabsTrigger>
            <TabsTrigger value="projects"><Briefcase className="h-4 w-4 mr-1" /> Projets</TabsTrigger>
            <TabsTrigger value="experience"><GraduationCap className="h-4 w-4 mr-1" /> Parcours</TabsTrigger>
            <TabsTrigger value="services"><Wrench className="h-4 w-4 mr-1" /> Services</TabsTrigger>
            <TabsTrigger value="testimonials"><Quote className="h-4 w-4 mr-1" /> Témoignages</TabsTrigger>
            <TabsTrigger value="leads"><Mail className="h-4 w-4 mr-1" /> Leads</TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="mt-4"><GeneralTab /></TabsContent>
          <TabsContent value="sessions" className="mt-4">
            <div className="rounded-xl border bg-card overflow-hidden">
              <TrainingSessionsContent />
            </div>
          </TabsContent>
          <TabsContent value="formations" className="mt-4">
            <EntitySection icon={BookOpen} title="Formations (catalogue)" description="Tes offres de formation présentées sur la vitrine (indépendantes des sessions datées)." aiKind="formation" api={{ list: showcaseAPI.listFormations, create: showcaseAPI.createFormation, update: showcaseAPI.updateFormation, delete: showcaseAPI.deleteFormation }} fields={formationFields} renderSubtitle={(i) => [i.level, i.duration, i.price_from ? `${i.price_from} ${i.currency || 'EUR'}` : null].filter(Boolean).join(' · ')} />
          </TabsContent>
          <TabsContent value="projects" className="mt-4">
            <EntitySection icon={Briefcase} title="Projets / Portfolio" description="Tes réalisations, cas clients, démos." aiKind="project" api={{ list: showcaseAPI.listProjects, create: showcaseAPI.createProject, update: showcaseAPI.updateProject, delete: showcaseAPI.deleteProject }} fields={projectFields} renderSubtitle={(i) => [i.category, (i.tech_stack || []).join(' · ')].filter(Boolean).join(' — ')} />
          </TabsContent>
          <TabsContent value="experience" className="mt-4">
            <EntitySection icon={GraduationCap} title="Parcours / Expérience" description="Expériences, formations, certifications." aiKind="experience" aiTitleField="role" api={{ list: showcaseAPI.listExperience, create: showcaseAPI.createExperience, update: showcaseAPI.updateExperience, delete: showcaseAPI.deleteExperience }} fields={experienceFields} renderSubtitle={(i) => [i.company, i.period_start].filter(Boolean).join(' · ')} />
          </TabsContent>
          <TabsContent value="services" className="mt-4">
            <EntitySection icon={Wrench} title="Services" description="Prestations : audit, coaching, mise en place…" aiKind="service" api={{ list: showcaseAPI.listServices, create: showcaseAPI.createService, update: showcaseAPI.updateService, delete: showcaseAPI.deleteService }} fields={serviceFields} renderSubtitle={(i) => i.price_from ? `À partir de ${i.price_from} ${i.currency || 'EUR'}` : (i.cta_label || '')} />
          </TabsContent>
          <TabsContent value="testimonials" className="mt-4">
            <EntitySection icon={Quote} title="Témoignages" description="Avis clients pour la crédibilité." api={{ list: showcaseAPI.listTestimonials, create: showcaseAPI.createTestimonial, update: showcaseAPI.updateTestimonial, delete: showcaseAPI.deleteTestimonial }} fields={testimonialFields} renderSubtitle={(i) => [i.author_role, i.company].filter(Boolean).join(', ')} />
          </TabsContent>
          <TabsContent value="leads" className="mt-4"><LeadsTab /></TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
}
